import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

interface Props<T> {
  items: T[];
  value: T | null;
  onChange: (item: T | null) => void;
  getLabel: (item: T) => string;
  getKey: (item: T) => string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Searchable single-select dropdown — mirrors the Power Apps Classic ComboBox
 * (chevron, click to open the list, type to filter, pick one). Used for the
 * client / billing-client pickers.
 */
export function ComboBox<T>({
  items, value, onChange, getLabel, getKey, placeholder = 'Select…', disabled, loading,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setQuery(''); }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = query.trim()
    ? items.filter((i) => getLabel(i).toLowerCase().includes(query.toLowerCase()))
    : items;

  function toggle() {
    if (disabled) return;
    setOpen((o) => {
      const next = !o;
      if (next) { setQuery(''); inputRef.current?.focus(); }
      return next;
    });
  }

  return (
    <div className="relative" ref={ref}>
      <div
        className={`input flex items-center gap-1 pr-1 ${disabled ? 'cursor-not-allowed border-transparent !bg-muted text-muted-foreground' : 'cursor-pointer'}`}
        onClick={() => { if (!open) toggle(); }}
      >
        <input
          ref={inputRef}
          className="min-w-0 flex-1 bg-transparent outline-none disabled:cursor-not-allowed"
          disabled={disabled}
          placeholder={disabled ? '' : placeholder}
          value={open ? query : value ? getLabel(value) : ''}
          onFocus={() => { if (!disabled) setOpen(true); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value) onChange(null); }}
        />
        {value && !disabled ? (
          <button
            type="button"
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); onChange(null); setQuery(''); }}
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggle(); }}
        />
      </div>
      {open && !disabled && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-card shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">No matches</div>
          ) : (
            filtered.slice(0, 300).map((i) => (
              <button
                type="button"
                key={getKey(i)}
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-secondary"
                onClick={() => { onChange(i); setOpen(false); setQuery(''); }}
              >
                {getLabel(i)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
